import { useCallback, useState } from 'react';

export type BindBirthdayInput = {
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
};

export const useBirthdayInput = (
  initialValue: string,
  field: string,
  onChangeHook?: (field: string, value: string) => void,
  onBlurHook?: (field: string, value: string) => void,
) => {
  const [value, setValue] = useState(initialValue);
  const template = 'MM/DD/YYYY';

  const validateDay = useCallback(val => {
    if (val && val.replace(/D/g, '').length === 2) {
      const validDay = Math.min(Math.max(Number(val), 1), 31);
      return validDay < 10 ? '0' + validDay : validDay;
    }
    return val;
  }, []);

  const validateMonth = useCallback(val => {
    if (val && val.replace(/M/g, '').length === 2) {
      const validMonth = Math.min(Math.max(Number(val), 1), 12);
      return validMonth < 10 ? '0' + validMonth : validMonth;
    }
    return val;
  }, []);

  const validateYear = useCallback(val => {
    if (val && val.replace(/Y/g, '').length === 4) {
      const validYear = Math.min(Math.max(Number(val), 1900), 2020);
      return validYear < 10 ? '0' + validYear : validYear;
    }
    return val;
  }, []);

  const isValid = useCallback((val: string) => (val && !isNaN(Number(val)) ? val : null), []);

  const getFormattedDate = useCallback(
    (date: string[]) => {
      const month = `${isValid(date[0]) ?? 'M'}${isValid(date[1]) ?? 'M'}`;
      const day = `${isValid(date[2]) ?? 'D'}${isValid(date[3]) ?? 'D'}`;
      const year = `${isValid(date[4]) ?? 'Y'}${isValid(date[5]) ?? 'Y'}${isValid(date[6]) ?? 'Y'}${
        isValid(date[7]) ?? 'Y'
      }`;
      return `${validateMonth(month)}/${validateDay(day)}/${validateYear(year)}`;
    },
    [isValid, validateDay, validateMonth, validateYear],
  );

  return {
    value,
    setValue,
    reset: () => setValue(''),
    bind: {
      value,
      onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.nativeEvent.code === 'Backspace') {
          setValue(prev => {
            const cursorPosition = (event.target as HTMLInputElement).selectionStart;
            if (cursorPosition === 3 || cursorPosition === 6) {
              event.preventDefault && event.preventDefault();
            }
            const prevArr = prev.replace(/M/g, '').replace(/D/g, '').replace(/Y/g, '').replace(/\//g, '').split('');
            let dateArr = value.split('/');
            const format = template.split('/');
            dateArr[0] = (dateArr[0] + format[0]).slice(0, 2);
            dateArr[1] = (dateArr[1] + format[1]).slice(0, 2);
            if (dateArr[2].length < 4) {
              dateArr[2] = (dateArr[2] + format[2]).slice(0, 4);
            }
            dateArr = dateArr.join('').split('');
            if (event.nativeEvent.code === 'Backspace' && [2, 4].includes(prevArr.length)) {
              event.preventDefault && event.preventDefault();
              dateArr[prevArr.length - 1] = template.split('')[dateArr.length - 1];
            }
            return getFormattedDate(dateArr);
          });
        }
      },
      onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
        let cursorPosition = event.target.selectionStart;
        if (cursorPosition === 2 || cursorPosition === 5) {
          cursorPosition += 1;
        }
        let dateArr = [''];
        if (event.target.value) {
          const newVal = event.target.value.split('/');
          const format = template.split('/');
          if (newVal[0] && newVal[0].length > 2) {
            newVal[0] = newVal[0].replace('M', '');
          }
          if (newVal[1] && newVal[1].length > 2) {
            newVal[1] = newVal[1].replace('D', '');
          }
          newVal[0] = (newVal[0] + format[0]).slice(0, 2);
          newVal[1] = (newVal[1] + format[1]).slice(0, 2);
          if (newVal[2] && newVal[2].length < 4) {
            newVal[2] = (newVal[2] + format[2]).slice(0, 4);
          }
          dateArr = newVal.join('').split('');
        }
        setValue(() => {
          return getFormattedDate(dateArr);
        });
        if (onChangeHook) {
          onChangeHook(field, getFormattedDate(dateArr));
        }
        setTimeout(() => {
          event.target.selectionStart = cursorPosition;
          event.target.selectionEnd = cursorPosition;
        });
      },
      onFocus: () => {
        setValue(val => (val === '' ? template : val));
      },
      onBlur: () => {
        setValue(val => (val === template ? '' : val));
        onBlurHook && onBlurHook(field, value);
      },
    },
  };
};
